import { useTranslation } from 'react-i18next';
import { Counterpart } from '~/types/counterparts';
import { Document } from '~/types/document';
import { BookkeepingAccount, BookkeepingEntrySource } from '~/types/general-bookkeeping';
import { Money } from '~/types/money';
import { isAvere, isDare } from '~/utils/bookkeeping/bookkeeping-entry';
import { absMoney, formatMoney } from '~/utils/money';
import styles from './BookkeepingTransactionEntriesPreviewTable.module.scss';

export interface BookkeepingTransactionPreviewEntry {
  bookkeepingAccount: BookkeepingAccount | null;
  amount: Money;
  counterpart?: Counterpart;
  document?: Document;
  source: BookkeepingEntrySource;
}

interface Props {
  entries: BookkeepingTransactionPreviewEntry[];
}

const formatBookkeepingAccountLabel = (account: BookkeepingAccount) => {
  return `${account.code} - ${account.name}`;
};

const BookkeepingTransactionEntriesPreviewTable = ({ entries }: Props) => {
  const { t } = useTranslation('bookkeeping', { keyPrefix: 'transactions.table.preview' });
  const { t: documentsT } = useTranslation('documents');

  return (
    <table className={styles.table} aria-label={t('title')}>
      <thead>
        <tr>
          <th>{t('bookkeeping_account')}</th>
          <th className={styles.amount}>{t('debit')}</th>
          <th className={styles.amount}>{t('credit')}</th>
          <th>{t('counterpart')}</th>
          <th>{t('document')}</th>
        </tr>
      </thead>
      <tbody>
        {entries.map((entry, index) => {
          return (
            <tr key={index}>
              <td>{entry.bookkeepingAccount && formatBookkeepingAccountLabel(entry.bookkeepingAccount)}</td>
              <td className={styles.amount}>{isDare(entry) && formatMoney(absMoney(entry.amount))}</td>
              <td className={styles.amount}>{isAvere(entry) && formatMoney(absMoney(entry.amount))}</td>
              <td>{entry.counterpart?.companyName}</td>
              <td>
                {entry.document && `${documentsT(`type.${entry.document.documentType}`)} ${entry.document.number}`}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export default BookkeepingTransactionEntriesPreviewTable;
